import React, { useEffect, useState } from "react";
import {
  clearBluetoothPrinter,
  getBluetoothPrinterName,
  isBluetoothSupported,
  pairBluetoothPrinter,
} from "../pages/pos/bluetoothPrinter";
import {
  getPrintMethod,
  PrintMethod,
  setPrintMethod,
} from "../pages/pos/universalPrint";

interface Props {
  onClose: () => void;
}

const METHODS: { value: PrintMethod; label: string; description: string }[] = [
  // {
  //   value: "qz",
  //   label: "QZ Tray (Desktop)",
  //   description: "Silent auto-print via QZ Tray. Requires QZ Tray installed on PC/Mac.",
  // },
  // {
  //   value: "rawbt",
  //   label: "RawBT (Android — Recommended)",
  //   description:
  //     "Best for Android tablets. Prints via RawBT app using Bluetooth Classic (SPP). Install RawBT from Play Store, pair your printer in RawBT, then select this.",
  // },
  {
    value: "bluetooth",
    label: "Web Bluetooth BLE (Android)",
    description:
      "Direct BLE printing. Only works if your printer supports Bluetooth LE. Requires one-time pairing below.",
  },
  // {
  //   value: "system",
  //   label: "System Print Dialog",
  //   description:
  //     "Uses the browser print dialog. Works on all platforms including iOS (AirPrint).",
  // },
];

const PrinterSettings: React.FC<Props> = ({ onClose }) => {
  const [method, setMethod] = useState<PrintMethod>(getPrintMethod);
  const [btName, setBtName] = useState<string | null>(getBluetoothPrinterName);
  const [pairing, setPairing] = useState(false);
  const [pairError, setPairError] = useState<string | null>(null);

  useEffect(() => {
    setPrintMethod(method);
  }, [method]);

  const handlePair = async () => {
    setPairing(true);
    setPairError(null);
    try {
      const name = await pairBluetoothPrinter();
      setBtName(name);
      setMethod("bluetooth");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Pairing failed";
      setPairError(msg);
    } finally {
      setPairing(false);
    }
  };

  const handleClear = () => {
    clearBluetoothPrinter();
    setBtName(null);
    if (method === "bluetooth") setMethod("qz");
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold">Printer Settings</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        {/* Method selector */}
        <p className="text-sm text-gray-500 mb-3">Print method</p>
        <div className="space-y-2 mb-6">
          {METHODS.map((m) => {
            const disabled = m.value === "bluetooth" && !isBluetoothSupported();
            return (
              <label
                key={m.value}
                className={`flex items-start gap-3 border rounded-xl p-3 cursor-pointer transition-colors ${
                  method === m.value
                    ? "border-black bg-gray-50"
                    : "border-gray-200 hover:border-gray-300"
                } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
              >
                <input
                  type="radio"
                  name="print_method"
                  value={m.value}
                  checked={method === m.value}
                  disabled={disabled}
                  onChange={() => setMethod(m.value)}
                  className="mt-1 accent-black"
                />
                <div>
                  <div className="font-medium text-sm">{m.label}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{m.description}</div>
                  {m.value === "bluetooth" && disabled && (
                    <div className="text-xs text-amber-600 mt-1">
                      Web Bluetooth requires Chrome on Android.
                    </div>
                  )}
                </div>
              </label>
            );
          })}
        </div>

        {/* RawBT info section */}
        {method === "rawbt" && (
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6 text-xs text-blue-800 space-y-1.5">
            <p className="font-semibold text-sm">Setup steps on Android:</p>
            <p>1. Install <strong>RawBT</strong> from Play Store (free)</p>
            <p>2. Open RawBT → Settings → select your printer → connect</p>
            <p>3. Come back here → tap <strong>Done</strong></p>
            <p>4. Every receipt will now print automatically via RawBT</p>
            <p className="text-blue-600 mt-2">Works with all Bluetooth thermal printers (Classic + BLE). No extra setup needed per order.</p>
          </div>
        )}

        {/* Bluetooth BLE pairing section */}
        {method === "bluetooth" && (
          <div className="border border-gray-200 rounded-xl p-4 mb-6">
            <p className="text-sm font-medium mb-2">Bluetooth Printer</p>
            {btName ? (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">
                  📱 {btName}
                </span>
                <button
                  onClick={handleClear}
                  className="text-xs text-red-500 hover:text-red-700 underline"
                >
                  Remove
                </button>
              </div>
            ) : (
              <p className="text-xs text-gray-400 mb-3">No printer paired yet.</p>
            )}
            <button
              onClick={handlePair}
              disabled={pairing}
              className="mt-3 w-full py-2 rounded-lg bg-black text-white text-sm font-medium disabled:opacity-50"
            >
              {pairing ? "Searching…" : btName ? "Re-pair Printer" : "Pair Printer"}
            </button>
            {pairError && (
              <p className="text-xs text-red-500 mt-2">{pairError}</p>
            )}
            <p className="text-xs text-gray-400 mt-3">
              Tap "Pair Printer" to open the Bluetooth device picker. After pairing
              once, receipts will print automatically — no dialog needed.
            </p>
          </div>
        )}

        {/* iOS note */}
        {method === "system" && (
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 mb-6 text-xs text-blue-700">
            <strong>iOS / AirPrint:</strong> When an order is placed, the system
            print dialog will appear. Select your Bluetooth printer and tap Print.
            Make sure the printer is set up in iOS Settings → Bluetooth.
          </div>
        )}

        <button
          onClick={onClose}
          className="w-full py-3 rounded-xl bg-black text-white font-medium text-sm"
        >
          Done
        </button>
      </div>
    </div>
  );
};

export default PrinterSettings;
